import React, { useState, useEffect } from "react";
import axios from "axios";
import config from "../../config";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  IconButton,
  Button,
  Card,
  CardContent
} from "@mui/material";

import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import EditIcon from "@mui/icons-material/Edit";

const company = JSON.parse(sessionStorage.getItem("companyDetails"));

function FancyPagination({ count, page, rowsPerPage, onPageChange, maxButtons = 5 }) {

  const totalPages = Math.ceil(count / rowsPerPage);
  if (totalPages === 0) return null;

  let startPage = Math.max(0, page - Math.floor(maxButtons / 2));
  let endPage = Math.min(totalPages, startPage + maxButtons);

  if (endPage - startPage < maxButtons) {
    startPage = Math.max(0, endPage - maxButtons);
  }

  const pageNumbers = [];
  for (let i = startPage; i < endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={1} flexWrap="wrap">

      <IconButton onClick={() => onPageChange(null, 0)} disabled={page === 0}>
        <FirstPageIcon />
      </IconButton>

      <IconButton onClick={() => onPageChange(null, Math.max(0, page - 1))} disabled={page === 0}>
        <KeyboardArrowLeft />
      </IconButton>

      {pageNumbers.map((num) => (
        <Button
          key={num}
          variant={num === page ? "contained" : "outlined"}
          onClick={() => onPageChange(null, num)}
          size="small"
        >
          {num + 1}
        </Button>
      ))}

      <IconButton onClick={() => onPageChange(null, Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}>
        <KeyboardArrowRight />
      </IconButton>

      <IconButton onClick={() => onPageChange(null, totalPages - 1)} disabled={page >= totalPages - 1}>
        <LastPageIcon />
      </IconButton>

    </Box>
  );
}

export default function RoomList() {

  const [roomsData, setRoomsData] = useState([]);
  const [page, setPage] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);

  const [openModal, setOpenModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [wifiPass, setWifiPass] = useState("");
  const [roomNumber, setRoomNumber] = useState("");

  const rowsPerPage = 20;

  useEffect(() => {

    const fetchRooms = async () => {

      try {

        const token = sessionStorage.getItem("token");

        const response = await axios.get(
          `${config.API_BASE_URL}/home/GetAllRoom/${encodeURIComponent(company?.id)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setRoomsData(response.data);

      } catch (error) {

        console.error("Error fetching rooms:", error);

      } finally {

        setLoading(false);

      }

    };

    fetchRooms();

  }, []);

  const handleOpenModal = (room) => {
    setSelectedRoom(room);
    setWifiPass(room.wifipass || "");
    setRoomNumber(room.roomnumber || "");
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };




  const handleUpdate = async () => {
    const token = sessionStorage.getItem("token");
    const payload = {
      id: selectedRoom.hotelid,
      roomnumber: roomNumber,
      wifiPass: wifiPass
    };

    try {
      await fetch(`${config.API_BASE_URL}/home/UpdateRoom`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      window.location.reload();

    } catch (err) {
      console.error(err);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
    setPage(0);
  };

  const filteredRooms = roomsData.filter((room) =>
    Object.values(room).some(
      (value) =>
        value !== null &&
        value !== undefined &&
        value.toString().toLowerCase().includes(searchText.toLowerCase())
    )
  );

  return (
    <Box p={3}>

      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>

          <Typography variant="h4" fontWeight="bold" mb={2}>
            Room Management
          </Typography>

          <TextField
            label="Search Rooms"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChange}
            sx={{ width: 300 }}
          />

        </CardContent>
      </Card>

      {loading ? (

        <Box display="flex" justifyContent="center" mt={5}>
          <CircularProgress />
        </Box>

      ) : (

        <Paper sx={{ borderRadius: 3, overflow: "hidden", border: "1px solid #dcdcdc" }}>

          <TableContainer>

            <Table sx={{ border: "1px solid #dcdcdc" }}>

              <TableHead>

                <TableRow sx={{ background: "linear-gradient(45deg,#1976d2,#42a5f5)" }}>

                  {[
                    "Hotel Name",
                    "Room Number",
                    "Type",
                    "AC Type",
                    "Capacity",
                    "Refundable",
                    "Cancellation Policy",
                    "Wifi",
                    "Action"
                  ].map((head) => (

                    <TableCell
                      key={head}
                      align="center"
                      sx={{ color: "white", fontWeight: "bold", border: "1px solid #e0e0e0" }}
                    >
                      {head}
                    </TableCell>

                  ))}

                </TableRow>

              </TableHead>

              <TableBody>

                {filteredRooms
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((room, i) => (

                    <TableRow
                      key={i}
                      sx={{
                        backgroundColor: i % 2 ? "#f8fbff" : "white",
                        "&:hover": { backgroundColor: "#e3f2fd", transition: "0.3s" }
                      }}
                    >

                      <TableCell align="center">{room.hotelName}</TableCell>
                      <TableCell align="center">
                        {room.roomnumber}
                        {room.roomName ? ` (${room.roomName})` : ""}
                      </TableCell>
                      <TableCell align="center">{room.type}</TableCell>
                      <TableCell align="center">{room.acType}</TableCell>
                      <TableCell align="center">{room.capacity}</TableCell>
                      <TableCell align="center">{room.refundable ? "Yes" : "No"}</TableCell>
                      <TableCell align="center">{room.cancellationPolicy}</TableCell>
                      <TableCell align="center">{room.wifipass}</TableCell>

                      <TableCell align="center">
                        <IconButton
                          color="primary"
                          onClick={() => handleOpenModal(room)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>

                    </TableRow>

                  ))}

              </TableBody>

            </Table>

          </TableContainer>

          <FancyPagination
            count={filteredRooms.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
          />

        </Paper>

      )}

      <Dialog open={openModal} onClose={handleCloseModal}>

        <DialogTitle>Update Room</DialogTitle>

        <DialogContent>

          <TextField
            margin="dense"
            label="Room Number"
            fullWidth
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
          />

          <TextField
            margin="dense"
            label="Wifi Password"
            fullWidth
            value={wifiPass}
            onChange={(e) => setWifiPass(e.target.value)}
          />

        </DialogContent>

        <DialogActions>

          <Button onClick={handleCloseModal}>Cancel</Button>

          <Button variant="contained" onClick={handleUpdate}>
            Update
          </Button>

        </DialogActions>

      </Dialog>

    </Box>
  );
}